/**
 * Error Reporter for Frontend
 *
 * Handles error reporting to Slack via Cloudflare function
 * Detects backend errors and converts them to user-friendly messages
 */

import axios from 'axios';

interface ErrorReportOptions {
  context: string;
  error: any;
  additionalInfo?: Record<string, any>;
  showUserMessage?: boolean;
  kind?: 'error' | 'api-outage';
}

interface UserFriendlyError {
  userMessage: string;
  shouldReport: boolean;
}

const SLACK_REPORT_DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const API_OUTAGE_DEDUPE_WINDOW_MS = 15 * 60 * 1000;
const recentReports = new Map<string, number>();
let lastApiOutageReportedAt = 0;

function isReactSnap(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof document !== 'undefined' &&
    Boolean((window as any).__REACT_SNAP__)
  );
}

function isCanceledRequest(error: any): boolean {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    Boolean(axios.isCancel?.(error))
  );
}

/**
 * True for errors that mean the API host is down or unreachable.
 * Application 4xx/500s are excluded so backend Slack still owns those.
 */
export function isApiOutageError(error: any): boolean {
  if (!axios.isAxiosError(error) || isCanceledRequest(error)) {
    return false;
  }

  const status = error.response?.status;
  if (!error.response) {
    return true;
  }

  return status === 502 || status === 503 || status === 504;
}

function shouldSendOutageAlert(): boolean {
  const now = Date.now();
  if (now - lastApiOutageReportedAt < API_OUTAGE_DEDUPE_WINDOW_MS) {
    return false;
  }
  lastApiOutageReportedAt = now;
  return true;
}

/**
 * Check if an error is from the backend API
 */
function isBackendError(error: any): boolean {
  // Backend errors typically have response data or are axios errors
  if (error?.response?.data || error?.response?.status) {
    return true;
  }

  // Check if error message contains backend-specific patterns
  const errorMessage = error?.message || String(error);
  const backendPatterns = [
    'Failed to create template',
    'Failed to fetch template',
    'Failed to update template',
    'Failed to create variation',
    'Failed to create variant',
    'Failed to list templates',
    'Failed to fetch billing info',
    'Failed to list API keys',
    'Failed to fetch user profile',
    'Template not found',
    'Variant not found',
    'Layer not found',
  ];

  return backendPatterns.some(pattern => errorMessage.includes(pattern));
}

function getErrorFingerprint(options: ErrorReportOptions): string {
  const error = options.error;
  const errorType = error?.name || error?.constructor?.name || 'Error';
  const errorMessage = error?.message || String(error);
  return `${options.context}::${errorType}::${errorMessage}`;
}

function shouldSendBasedOnDedupe(options: ErrorReportOptions): boolean {
  const now = Date.now();
  const fingerprint = getErrorFingerprint(options);
  const lastSentAt = recentReports.get(fingerprint);

  if (lastSentAt && now - lastSentAt < SLACK_REPORT_DEDUPE_WINDOW_MS) {
    return false;
  }

  recentReports.set(fingerprint, now);

  // Periodic cleanup to keep memory bounded during long sessions.
  for (const [key, timestamp] of recentReports.entries()) {
    if (now - timestamp > SLACK_REPORT_DEDUPE_WINDOW_MS * 2) {
      recentReports.delete(key);
    }
  }

  return true;
}

/**
 * Coerce FastAPI `detail` (string | object | validation array) to a searchable string.
 */
function detailToString(detail: unknown, fallback = ''): string {
  if (typeof detail === 'string') return detail;
  if (detail == null) return fallback;
  if (Array.isArray(detail)) {
    return detail
      .map(item =>
        typeof item === 'string'
          ? item
          : item && typeof item === 'object' && 'msg' in item
            ? String((item as { msg: unknown }).msg)
            : JSON.stringify(item)
      )
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') {
    const obj = detail as { message?: unknown; detail?: unknown };
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.detail === 'string') return obj.detail;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return String(detail);
}

/**
 * Check if error message matches any of the given patterns
 */
function matchesAny(message: string, patterns: string[]): boolean {
  const haystack = message.toLowerCase();
  return patterns.some(pattern => haystack.includes(pattern.toLowerCase()));
}

/**
 * Handle 400 errors - Invalid Canva share URL
 */
function handle400ShareUrlError(fullErrorMessage: string): UserFriendlyError {
  const infoLink = ' [See instructions](#canva-share-url-info)';

  // No document in Canva response (Brand Template / Brand Kit access, private design, etc.)
  if (
    matchesAny(fullErrorMessage, [
      'did not return any design content',
      'brand template',
      'brand kit',
    ])
  ) {
    return {
      userMessage:
        'This design is not properly shared. Please make sure the design is set to "Anyone with the link" in Canva.',
      shouldReport: false,
    };
  }

  // Invalid format errors
  if (
    matchesAny(fullErrorMessage, [
      'must contain',
      '/design/',
      'not a Canva share URL',
    ])
  ) {
    return {
      userMessage: `This is not a valid Canva share URL. For canva.com, use a view link with "/design/" in the URL, or paste a full canva.link share link.${infoLink}`,
      shouldReport: false,
    };
  }

  // Access/permission errors
  if (
    matchesAny(fullErrorMessage, [
      'access is denied',
      'not properly shared',
      'requires authentication',
    ])
  ) {
    return {
      userMessage: `This design is not properly shared. Please make sure the design is set to "Anyone with the link" in Canva.${infoLink}`,
      shouldReport: false,
    };
  }

  // Not found errors
  if (matchesAny(fullErrorMessage, ['not found', '404'])) {
    return {
      userMessage:
        'Design not found. Please check that the URL is correct and the design still exists.',
      shouldReport: false,
    };
  }

  // Generic share URL error
  if (
    matchesAny(fullErrorMessage, [
      'share URL',
      'not properly shared',
      'not accessible',
    ])
  ) {
    return {
      userMessage: `Unable to access this Canva design. Please make sure it is a share URL.${infoLink}`,
      shouldReport: false,
    };
  }

  // Invalid domain errors
  if (matchesAny(fullErrorMessage, ['Invalid Canva URL', 'allowed domains'])) {
    return {
      userMessage:
        'Invalid Canva URL. Only URLs from canva.com and canva.link domains are allowed.',
      shouldReport: false,
    };
  }

  return {
    userMessage: `Unable to access this Canva design. Please make sure it is a share URL.${infoLink}`,
    shouldReport: false,
  };
}

/**
 * Convert backend errors to user-friendly messages
 */
function getUserFriendlyMessage(
  error: any,
  reportContext?: string
): UserFriendlyError {
  // Font list / preload hits third-party CDNs and often fails for ad blockers or flaky networks — not actionable in Slack
  if (
    reportContext === 'Preloading font' ||
    reportContext === 'Loading fonts list' ||
    reportContext === 'Fetching Google Fonts from Supabase' ||
    reportContext === 'Fetching custom fonts from Supabase' ||
    reportContext === 'Loading OpenAPI spec'
  ) {
    return {
      userMessage:
        reportContext === 'Loading OpenAPI spec'
          ? 'API documentation could not be loaded. Please refresh the page.'
          : 'Font preview could not be loaded. Try again or choose another font.',
      shouldReport: false,
    };
  }

  // Axios with no HTTP response: offline, connection reset, browser connection limits, etc.
  if (axios.isAxiosError(error) && !error.response) {
    return {
      userMessage: 'Network error. Please check your connection and try again.',
      shouldReport: false,
    };
  }

  const errorMessage = error?.message || String(error);
  const errorDetail = error?.response?.data?.detail;
  const statusCode = error?.response?.status || error?.status;
  const fullErrorMessage = detailToString(errorDetail, errorMessage);
  const normalizedMessage = fullErrorMessage.toLowerCase();

  // Client clock ahead of Supabase — stale JWT rejected; fonts use anon client now
  if (normalizedMessage.includes('jwt issued at future')) {
    return {
      userMessage: 'Font list could not be loaded. Please refresh the page.',
      shouldReport: false,
    };
  }

  // Backend errors - don't report to Slack (already reported from backend)
  if (isBackendError(error)) {
    // Status code specific handlers
    if (statusCode === 404 || normalizedMessage.includes('not found')) {
      // Check if this is a Canva URL related error
      const isCanvaRelated =
        normalizedMessage.includes('canva') ||
        errorMessage.toLowerCase().includes('canva') ||
        error?.config?.url?.includes('/template') ||
        errorMessage.includes('template');

      if (isCanvaRelated) {
        return {
          userMessage:
            'Canva url not found. Please check that the URL is correct and the design still exists.',
          shouldReport: false,
        };
      }

      return {
        userMessage: 'Resource not found. Please try again.',
        shouldReport: false,
      };
    }

    if (statusCode === 422) {
      if (normalizedMessage.includes('design type is not supported')) {
        return {
          userMessage:
            'Design type is not supported. See [limitations](/limitations/)',
          shouldReport: false,
        };
      }
      return {
        userMessage:
          'Unable to process this request. Please double-check and try again.',
        shouldReport: false,
      };
    }

    if (statusCode === 403) {
      // Plan / seat / org limits: backend sends a clear message we can show as-is
      if (
        fullErrorMessage &&
        (normalizedMessage.includes('plan limit') ||
          normalizedMessage.includes('credits') ||
          normalizedMessage.includes('upgrade your plan') ||
          normalizedMessage.includes('plus or pro') ||
          normalizedMessage.includes('seat') ||
          normalizedMessage.includes('organizations require'))
      ) {
        return {
          userMessage: fullErrorMessage,
          shouldReport: false,
        };
      }
      return {
        userMessage: 'You are not authorized to access this resource.',
        shouldReport: false,
      };
    }

    if (statusCode === 400) {
      // Check for share URL errors
      if (
        matchesAny(fullErrorMessage, [
          'share URL',
          'not a Canva share URL',
          'not properly shared',
          'not accessible',
          'access is denied',
          'requires authentication',
          'must contain',
          '/design/',
          'Invalid Canva URL',
          'allowed domains',
          'did not return any design content',
          'brand template',
          'brand kit',
        ])
      ) {
        return handle400ShareUrlError(fullErrorMessage);
      }
    }

    // Content-based error handlers
    if (matchesAny(fullErrorMessage, ['variation', 'variant'])) {
      return {
        userMessage: 'Failed to generate variation. Please try again.',
        shouldReport: false,
      };
    }

    if (matchesAny(fullErrorMessage, ['Canva', 'template'])) {
      return {
        userMessage:
          'Unable to process your design. Please check the URL and try again.',
        shouldReport: false,
      };
    }

    // Generic backend error
    return {
      userMessage: 'An error occurred. Please try again.',
      shouldReport: false,
    };
  }

  // Frontend errors - should be reported
  // Expected auth-state transitions (e.g. email sign-up before confirmation)
  // should not alert Slack.
  if (
    matchesAny(fullErrorMessage, [
      'Authentication required. Please sign in.',
      'No valid session found. Please sign in again.',
    ])
  ) {
    return {
      userMessage: 'Please sign in to continue.',
      shouldReport: false,
    };
  }

  // Network errors (non-Axios, e.g. fetch wrappers)
  // Safari: "Load failed"; Chrome: "Failed to fetch"; Firefox: "NetworkError when attempting to fetch resource"
  if (
    normalizedMessage.includes('network') ||
    normalizedMessage.includes('fetch') ||
    normalizedMessage.includes('load failed') ||
    normalizedMessage.includes('networkerror')
  ) {
    return {
      userMessage: 'Network error. Please check your connection and try again.',
      shouldReport: false,
    };
  }

  // Timeout errors
  if (normalizedMessage.includes('timeout')) {
    return {
      userMessage: 'Request timed out. Please try again.',
      shouldReport: true,
    };
  }

  // Font loading errors (non-preload paths, e.g. canvas export)
  if (normalizedMessage.includes('font')) {
    return {
      userMessage: 'Failed to load fonts. Please refresh the page.',
      shouldReport: false,
    };
  }

  // Storage/localStorage errors
  if (
    normalizedMessage.includes('storage') ||
    normalizedMessage.includes('quota')
  ) {
    return {
      userMessage: 'Storage limit reached. Please clear your browser data.',
      shouldReport: true,
    };
  }

  // Generic frontend error
  return {
    userMessage: 'Something went wrong. Please try again.',
    shouldReport: true,
  };
}

/**
 * Report an error to Slack via Cloudflare function
 */
async function sendToSlack(options: ErrorReportOptions): Promise<void> {
  try {
    // Avoid firing network side effects during react-snap prerender.
    if (isReactSnap()) return;

    const error = options.error;
    const errorType = error?.name || error?.constructor?.name || 'Error';
    const errorMessage = error?.message || String(error);
    const stackTrace = error?.stack;

    const payload = {
      message: errorMessage,
      context: options.context,
      errorType,
      kind: options.kind || 'error',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      additionalInfo: options.additionalInfo || {},
      stackTrace,
    };

    // Send to Cloudflare function (don't await - fire and forget)
    fetch('/slack_notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      // Silently fail if Slack notification fails
    });
  } catch {
    // Don't let error reporting itself cause issues
  }
}

/**
 * Main error reporter function
 *
 * @param options - Error reporting options
 * @returns User-friendly error message to display
 */
export async function reportError(
  options: ErrorReportOptions
): Promise<string> {
  const { context, error, additionalInfo } = options;

  // Get user-friendly message and determine if we should report
  const { userMessage, shouldReport } = getUserFriendlyMessage(error, context);

  // Report to Slack if it's a frontend error
  if (shouldReport && shouldSendBasedOnDedupe(options)) {
    await sendToSlack({
      context,
      error,
      additionalInfo,
    });
  }

  // Return user-friendly message
  return userMessage;
}

/**
 * Convenience function for reporting errors in catch blocks
 *
 * Usage:
 * try {
 *   await someOperation();
 * } catch (err) {
 *   const userMessage = await reportError({
 *     context: 'Creating template',
 *     error: err,
 *     additionalInfo: { canvaUrl: 'https://...' }
 *   });
 *   setError(userMessage);
 * }
 */
export default reportError;

/**
 * Report error without returning a message (for silent reporting)
 */
export async function reportErrorSilent(
  options: Omit<ErrorReportOptions, 'showUserMessage'>
): Promise<void> {
  await reportError(options);
}

/**
 * Fire-and-forget Slack alert when the API host is down (502/503/504 / no response).
 * Client cooldown is 15 minutes so one outage does not spam from a single browser.
 */
export function reportApiOutage(error: any): void {
  if (isReactSnap() || !isApiOutageError(error) || !shouldSendOutageAlert()) {
    return;
  }

  void sendToSlack({
    context: 'API outage',
    error,
    kind: 'api-outage',
    additionalInfo: {
      status: error?.response?.status ?? 'no-response',
      apiPath: error?.config?.url,
      method: error?.config?.method,
    },
  });
}

export function resetErrorReporterStateForTests(): void {
  recentReports.clear();
  lastApiOutageReportedAt = 0;
}
